/**
 * OrdersContent - Component for managing orders
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bell, PlusCircle, ShoppingCart, FileText, Clock, User, Phone,
  Truck, Store, Home, Wallet, CreditCard, CheckCircle, Package,
  DollarSign, MessageSquare, Printer, Bot, Calendar, AlertCircle, Bike,
  XCircle, Trash2, Zap, Settings, MessageCircle, Pencil, History, Ear
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import KanbanBoard from '../KanbanBoard';
import PDVScreen from '../../PDVScreen';
import OrdersAutoAcceptModal from './orders/OrdersAutoAcceptModal';
import OrdersDetailsModal from './orders/OrdersDetailsModal';
import OrdersDriverModal from './orders/OrdersDriverModal';
import OrdersRejectModal from './orders/OrdersRejectModal';
import OrdersDeleteModal from './orders/OrdersDeleteModal';
import { useWhatsAppIntegration, generateWhatsAppLink } from '../../hooks/useWhatsAppIntegration';

import { getBackendUrl } from '../../utils/api';
import { StockAlertsBanner } from '../../StockManagement';

const API = `${getBackendUrl()}/api`;

const DEFAULT_PRINT_FORMAT = {
  escpos_enabled: true, bold_order_number: true, bold_customer_name: false,
  bold_product_name: true, bold_additionals: true, bold_observations: false,
  bold_total: true, show_datetime: true, show_phone: true, show_address: true,
  show_delivery_fee: true, show_subtotal: true, show_payment_method: true,
  show_observations: true, show_footer: true, header_text: '', footer_message: '',
};

// Função helper para garantir valores numéricos seguros
const safeNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

// Função helper para formatar preço com segurança
const formatPrice = (value) => {
  return safeNumber(value).toFixed(2);
};

// Função para sanitizar dados do pedido
const sanitizeOrder = (order) => {
  if (!order) return null;
  return {
    ...order,
    total: safeNumber(order.total),
    subtotal: safeNumber(order.subtotal),
    delivery_fee: safeNumber(order.delivery_fee),
    discount: safeNumber(order.discount),
    change_for: safeNumber(order.change_for),
    items: (order.items || []).map(item => ({
      ...item,
      price: safeNumber(item.price),
      quantity: safeNumber(item.quantity, 1),
      unit_price: safeNumber(item.unit_price || item.price),
      subtotal: safeNumber(item.subtotal || (safeNumber(item.price) * safeNumber(item.quantity, 1)))
    }))
  };
};

// Error Boundary Component
class OrdersErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('OrdersContent Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Erro ao carregar pedidos</h2>
          <p className="text-gray-400 mb-4">Ocorreu um erro ao exibir os pedidos. Tente recarregar a página.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Recarregar Página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const OrdersContent = ({ isPDVOpen, setIsPDVOpen }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [printFormat, setPrintFormat] = useState(DEFAULT_PRINT_FORMAT);

  useEffect(() => {
    const loadPrintFormat = async () => {
      try {
        const res = await axios.get(`${API}/impression/settings`);
        if (res.data?.print_format) {
          setPrintFormat({ ...DEFAULT_PRINT_FORMAT, ...res.data.print_format });
        }
      } catch (err) { /* usa defaults */ }
    };
    loadPrintFormat();
  }, []);
  
  // Entregadores
  const [deliveryDrivers, setDeliveryDrivers] = useState([]);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [pendingDeliveryOrder, setPendingDeliveryOrder] = useState(null);
  const [notifyingDriver, setNotifyingDriver] = useState(false);
  
  // Modal de Rejeição
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingOrder, setRejectingOrder] = useState(null);
  const [selectedRejectReason, setSelectedRejectReason] = useState('');
  const [customRejectMessage, setCustomRejectMessage] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  
  // Modal de Exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edição de pedido (reaproveita PDV)
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const canEditOrder = (order) => {
    if (!order) return false;
    const editable = ['pendente', 'em_preparo', 'em preparo', 'preparando'];
    if (!editable.includes(order.status)) return false;
    const isPaid = order.payment_status === 'paid' || order.status_pagamento === 'approved';
    if (isPaid) return false;
    return true;
  };

  const startEditOrder = (order) => {
    setEditingOrder(order);
    setIsPDVOpen(true);
  };

  useEffect(() => {
    if (showOrderDetails && selectedOrder?.id) {
      fetchOrderHistory(selectedOrder.id);
    } else {
      setOrderHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOrderDetails, selectedOrder?.id]);

  const fetchOrderHistory = async (orderId) => {
    if (!orderId) return;
    try {
      setLoadingHistory(true);
      const res = await axios.get(`${API}/orders/${orderId}/history`);
      setOrderHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setOrderHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // WhatsApp API Integration Hook
  const whatsappIntegration = useWhatsAppIntegration();
  
  // WhatsApp connection status (legacy - mantido para compatibilidade)
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  
  // Estados para notificação de pedidos pendentes
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = React.useRef(null);
  // Preferência de som de novo pedido (configurável em Configurações > Notificações)
  const notifSoundRef = React.useRef({ url: '/sounds/notification.mp3', volume: 0.7 });
  
  // Estados para Auto-Accept
  const [autoAcceptEnabled, setAutoAcceptEnabled] = useState(false);
  const [autoAcceptLoading, setAutoAcceptLoading] = useState(false);
  const [autoAcceptMessage, setAutoAcceptMessage] = useState('Seu pedido foi recebido e já está em produção! 🍕');
  const [autoAcceptSendWhatsApp, setAutoAcceptSendWhatsApp] = useState(true);
  const [autoAcceptNewOrderSound, setAutoAcceptNewOrderSound] = useState(true);
  const [showAutoAcceptSettings, setShowAutoAcceptSettings] = useState(false);
  const [savingAutoAcceptSettings, setSavingAutoAcceptSettings] = useState(false);
  // Refs para evitar closures obsoletos dentro do setInterval de polling
  const autoAcceptEnabledRef = React.useRef(false);
  const autoAcceptNewOrderSoundRef = React.useRef(true);
  // Conjunto de IDs de pedidos já vistos nesta sessão (null = ainda não inicializado)
  const seenOrderIdsRef = React.useRef(null);
  // Áudio: elemento reutilizável + flag de "desbloqueado" por gesto do usuário (autoplay policy)
  const audioUnlockedRef = React.useRef(false);

  // Motivos de rejeição predefinidos
  const REJECT_REASONS = [
    { id: 'sem_motoboy', icon: '🛵', label: 'Sem motoboy', message: 'Nossos entregadores estão indisponíveis no momento.' },
    { id: 'produto_esgotado', icon: '📦', label: 'Produto em falta', message: 'Infelizmente um ou mais produtos do seu pedido estão esgotados.' },
    { id: 'restaurante_fechando', icon: '🏪', label: 'Restaurante fechando', message: 'Estamos encerrando nosso expediente.' },
    { id: 'fora_area', icon: '📍', label: 'Fora da área', message: 'Desculpe, mas não conseguimos realizar entregas na sua região.' },
    { id: 'restaurante_lotado', icon: '🔥', label: 'Muitos pedidos', message: 'Estamos com alta demanda e não conseguimos atender novos pedidos no momento.' },
    { id: 'horario_fechado', icon: '⏰', label: 'Horário encerrado', message: 'Nosso horário de funcionamento encerrou.' },
    { id: 'problema_pagamento', icon: '💳', label: 'Problema no pagamento', message: 'Houve um problema com a forma de pagamento informada.' },
    { id: 'outro', icon: '✏️', label: 'Outro motivo', message: '' },
  ];

  // Retorna (criando na 1ª vez) o ÚNICO elemento de áudio reutilizável.
  // Reutilizar o mesmo elemento (trocando só o .src) é o que mantém o áudio
  // "desbloqueado" pelo navegador após o primeiro gesto do usuário.
  const getAudioEl = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    return audioRef.current;
  };

  // Aponta o elemento para a URL desejada (sem recriar o elemento).
  const setAudioSource = (el) => {
    const pref = notifSoundRef.current || {};
    const desiredUrl = pref.url || '/sounds/notification.mp3';
    if (el.dataset_src !== desiredUrl) {
      el.src = desiredUrl;
      el.dataset_src = desiredUrl;
    }
    return pref;
  };

  // "Desbloqueia" o áudio dentro de um gesto do usuário: toca mudo uma vez e pausa.
  // Depois disso o navegador permite play() programático (ex.: vindo do polling).
  const primeAudio = () => {
    if (audioUnlockedRef.current) return;
    try {
      const el = getAudioEl();
      setAudioSource(el);
      // Toca SEM mudo, porém com volume 0 (inaudível) dentro do gesto:
      // isso concede ao navegador a "ativação" para play() audível posterior.
      el.muted = false;
      const prevVolume = el.volume;
      el.volume = 0;
      const done = () => {
        try { el.pause(); el.currentTime = 0; } catch (e) { /* noop */ }
        el.volume = prevVolume;
        audioUnlockedRef.current = true;
      };
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.then(done).catch(() => { el.volume = prevVolume; /* ainda bloqueado; tenta no próximo gesto */ });
      } else {
        done();
      }
    } catch (e) { /* noop */ }
  };

  // Toca o som de notificação configurado da loja UMA vez (sem depender do sino de pendentes)
  const playStoreSound = () => {
    try {
      const el = getAudioEl();
      const pref = setAudioSource(el);
      const desiredVolume = typeof pref.volume === 'number' ? pref.volume : 0.7;
      el.muted = false;
      el.volume = desiredVolume;
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          console.log('🔊 [SOUND] play() OK (áudio tocando, não bloqueado)');
        }).catch(err => {
          console.log('🔇 [SOUND] play() BLOQUEADO/erro:', err && err.name ? err.name : err);
        });
      }
    } catch (err) {
      console.log('Erro ao tocar som de notificação:', err);
    }
  };

  // Função para tocar som de notificação personalizado (som recorrente de pendentes, respeita o sino)
  const playBuzzerSound = () => {
    if (!soundEnabled) return;
    playStoreSound();
  };

  // Carrega a preferência de som de novo pedido da loja
  const fetchNotificationSound = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/restaurant/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ns = response.data?.notification_sound;
      if (ns && ns.url) {
        notifSoundRef.current = {
          url: ns.url,
          volume: typeof ns.volume === 'number' ? ns.volume : 0.7
        };
      } else if (ns && typeof ns.volume === 'number') {
        notifSoundRef.current = { url: '/sounds/notification.mp3', volume: ns.volume };
      }
    } catch (err) {
      // fallback silencioso para o som padrão embutido
      console.log('Não foi possível carregar preferência de som:', err?.message);
    }
  };

  // Função para verificar pedidos pendentes e tocar buzina
  const checkPendingOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sanitizar dados
      const allOrders = (response.data || []).map(sanitizeOrder).filter(Boolean);
      
      const pendingOrders = allOrders.filter(order => order.status === 'pendente');
      
      if (pendingOrders.length > 0 && soundEnabled) {
        playBuzzerSound();
      }

      // Alerta sonoro ÚNICO de pedido novo (modo Auto-Aceitar ligado)
      // Controle independente do sino de pendentes.
      const currentIds = allOrders.map(o => o.id).filter(Boolean);
      if (seenOrderIdsRef.current === null) {
        // Primeira verificação: apenas registra os pedidos existentes, sem tocar.
        seenOrderIdsRef.current = new Set(currentIds);
      } else {
        const newIds = currentIds.filter(id => !seenOrderIdsRef.current.has(id));
        if (newIds.length > 0) {
          if (autoAcceptEnabledRef.current && autoAcceptNewOrderSoundRef.current) {
            console.log('🔔 [NEW-ORDER-SOUND] Novo pedido detectado — tocando alerta único:', newIds);
            playStoreSound();
          }
          newIds.forEach(id => seenOrderIdsRef.current.add(id));
        }
      }
      
      if (!statusFilter && !deliveryTypeFilter) {
        setOrders(allOrders);
      }
      
    } catch (error) {
      console.error('Error checking pending orders:', error);
    }
  };

  useEffect(() => {
    fetchNotificationSound();
    fetchOrders();
    fetchDeliveryDrivers();
    checkPendingOrders();
    
    const intervalId = setInterval(() => {
      checkPendingOrders();
    }, 20000);
    
    return () => {
      clearInterval(intervalId);
      // Apenas pausa; NÃO descarta o elemento (perderíamos o desbloqueio de áudio
      // obtido no gesto do usuário). O elemento é reutilizado entre re-execuções.
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (e) { /* noop */ }
      }
    };
  }, [soundEnabled]);
  
  useEffect(() => {
    fetchOrders();
  }, [statusFilter, deliveryTypeFilter]);

  // Buscar configurações de auto-accept
  useEffect(() => {
    fetchAutoAcceptSetting();
  }, []);

  // Mantém refs sincronizados p/ leitura dentro do polling (evita closures obsoletos)
  useEffect(() => {
    autoAcceptEnabledRef.current = autoAcceptEnabled;
  }, [autoAcceptEnabled]);
  useEffect(() => {
    autoAcceptNewOrderSoundRef.current = autoAcceptNewOrderSound;
  }, [autoAcceptNewOrderSound]);

  // Desbloqueia o áudio no PRIMEIRO gesto do usuário na página (política de autoplay do navegador).
  // Sem isso, o play() disparado pelo polling (fora de um clique) é bloqueado e nenhum som toca.
  useEffect(() => {
    const onGesture = () => {
      primeAudio();
      if (audioUnlockedRef.current) {
        window.removeEventListener('pointerdown', onGesture);
        window.removeEventListener('keydown', onGesture);
        window.removeEventListener('touchstart', onGesture);
      }
    };
    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('keydown', onGesture);
    window.addEventListener('touchstart', onGesture);
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      window.removeEventListener('touchstart', onGesture);
    };
  }, []);

  // Buscar entregadores ativos
  const fetchDeliveryDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/delivery-drivers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtrar apenas ativos
      setDeliveryDrivers((response.data || []).filter(d => d.active));
    } catch (error) {
      console.error('Error fetching delivery drivers:', error);
    }
  };

  // Notificar entregador via WhatsApp
  const notifyDeliveryDriver = async (driverId, orderId) => {
    try {
      setNotifyingDriver(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/delivery-drivers/${driverId}/notify?order_id=${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('🛵 Entregador notificado com sucesso!');
      } else {
        // Mostrar mensagem de erro específica
        const errorMsg = response.data.error || 'Erro ao notificar entregador';
        if (errorMsg.includes('WhatsApp não configurado')) {
          toast.error('⚠️ WhatsApp não está configurado para este restaurante. Configure nas Configurações.');
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      console.error('Error notifying driver:', error);
      const errorDetail = error.response?.data?.detail || error.response?.data?.error || 'Erro ao enviar notificação';
      toast.error(`Erro: ${errorDetail}`);
    } finally {
      setNotifyingDriver(false);
      setShowDriverModal(false);
      setPendingDeliveryOrder(null);
    }
  };

  // Selecionar entregador e notificar
  const handleSelectDriver = (driver) => {
    if (pendingDeliveryOrder) {
      notifyDeliveryDriver(driver.id, pendingDeliveryOrder.id);
    }
  };
  
  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
  };

  // ============= AUTO-ACCEPT FUNCTIONS =============
  const fetchAutoAcceptSetting = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/restaurant/auto-accept-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoAcceptEnabled(response.data.auto_accept_orders || false);
      setAutoAcceptMessage(response.data.auto_accept_message || 'Seu pedido foi recebido e já está em produção! 🍕');
      setAutoAcceptSendWhatsApp(response.data.auto_accept_send_whatsapp !== false);
      setAutoAcceptNewOrderSound(response.data.auto_accept_new_order_sound !== false);
    } catch (error) {
      console.error('Error fetching auto-accept setting:', error);
    }
  };
  
  const toggleAutoAccept = async () => {
    try {
      setAutoAcceptLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/restaurant/auto-accept-orders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoAcceptEnabled(response.data.auto_accept_orders);
      toast.success(
        response.data.auto_accept_orders 
          ? '⚡ Auto-aceitar pedidos ativado!' 
          : '⚡ Auto-aceitar pedidos desativado'
      );
    } catch (error) {
      console.error('Error toggling auto-accept:', error);
      toast.error('Erro ao alterar configuração');
    } finally {
      setAutoAcceptLoading(false);
    }
  };
  
  const saveAutoAcceptSettings = async () => {
    try {
      setSavingAutoAcceptSettings(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/restaurant/auto-accept-orders`, {
        auto_accept_message: autoAcceptMessage,
        auto_accept_send_whatsapp: autoAcceptSendWhatsApp,
        auto_accept_new_order_sound: autoAcceptNewOrderSound
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Configurações salvas!');
      setShowAutoAcceptSettings(false);
    } catch (error) {
      console.error('Error saving auto-accept settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingAutoAcceptSettings(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadError(null);
      const token = localStorage.getItem('token');
      let params = '';
      const filters = [];
      if (statusFilter) filters.push(`status=${statusFilter}`);
      if (deliveryTypeFilter) filters.push(`delivery_type=${deliveryTypeFilter}`);
      if (filters.length > 0) params = '?' + filters.join('&');
      
      const response = await axios.get(`${API}/orders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sanitizar todos os pedidos para evitar erros de renderização
      const sanitizedOrders = (response.data || []).map(sanitizeOrder).filter(Boolean);
      
      // Ordenar por data de criação (mais recente primeiro)
      // Garantir ordenação correta mesmo com formatos de data diferentes
      sanitizedOrders.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Mais recente primeiro
      });
      
      setOrders(sanitizedOrders);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoadError(error.message || 'Erro ao carregar pedidos');
      setLoading(false);
    }
  };

  const getFirstName = (fullName) => {
    if (!fullName) return 'Cliente';
    return fullName.trim().split(' ')[0];
  };
  
  const generateWhatsAppMessage = (order, newStatus) => {
    const firstName = getFirstName(order.customer_name);
    const isPickup = order.delivery_type === 'retirada' || order.delivery_type === 'pickup';
    
    const messages = {
      'em_producao': `🎉 Olá ${firstName}! Seu pedido está em produção e já estamos preparando com todo carinho! 😊`,
      'entrega': isPickup 
        ? `✅ ${firstName}, seu pedido está pronto para retirada! Estamos te esperando! 🏪`
        : `🚀 ${firstName}, seu pedido saiu para entrega! Logo logo está aí! 🛵`,
      'pronto': isPickup
        ? `🎉 ${firstName}, obrigado por retirar seu pedido! Volte sempre! 🤗`
        : `✅ ${firstName}, seu pedido foi entregue! Obrigado e volte sempre! 🤗`
    };
    
    return messages[newStatus] || `Olá ${firstName}! Status do seu pedido foi atualizado.`;
  };
  
  const updateOrderStatus = async (orderId, newStatus) => {
    // Debounce: prevenir duplo clique (bloqueia por 3 segundos)
    const debounceKey = `status_${orderId}_${newStatus}`;
    if (updateOrderStatus._locks?.[debounceKey]) {
      console.warn('Duplo clique bloqueado para:', debounceKey);
      return;
    }
    if (!updateOrderStatus._locks) updateOrderStatus._locks = {};
    updateOrderStatus._locks[debounceKey] = true;
    setTimeout(() => { delete updateOrderStatus._locks[debounceKey]; }, 3000);
    
    try {
      const token = localStorage.getItem('token');
      const order = orders.find(o => o.id === orderId);
      
      if (!newStatus) {
        newStatus = getNextStatus(order.status);
      }
      
      if (!newStatus) return;
      
      // Verificar se está mudando para "entrega" e é delivery (não retirada)
      const isDelivery = order.delivery_type !== 'retirada' && order.delivery_type !== 'pickup';
      const isChangingToDelivery = newStatus === 'entrega' && isDelivery;
      
      // Se está mudando para "entrega" e há entregadores cadastrados
      if (isChangingToDelivery && deliveryDrivers.length > 0) {
        // Primeiro, atualiza o status
        await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Se tiver apenas 1 entregador, notifica automaticamente
        if (deliveryDrivers.length === 1) {
          notifyDeliveryDriver(deliveryDrivers[0].id, orderId);
        } else {
          // Se tiver mais de 1, mostra modal para selecionar
          setPendingDeliveryOrder(order);
          setShowDriverModal(true);
        }
      } else {
        // Fluxo normal sem notificação de entregador
        await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Notificação WhatsApp é enviada pelo BACKEND automaticamente
      // (via send_order_status_notification no endpoint PATCH /orders/{id}/status)
      // Não enviar pelo frontend para evitar mensagens duplicadas
      
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return 'bg-yellow-500 text-white';
      case 'em_producao': return 'bg-blue-500 text-white';
      case 'entrega': return 'bg-purple-500 text-white';
      case 'pronto': return 'bg-green-500 text-white';
      case 'cancelado': return 'bg-red-500 text-white';
      case 'rejeitado': return 'bg-red-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Retorna o label do status baseado no tipo de entrega
  const getStatusLabel = (status, deliveryType = 'delivery') => {
    // Para status 'entrega', diferenciar entre delivery e retirada
    if (status === 'entrega') {
      if (deliveryType === 'retirada' || deliveryType === 'pickup') {
        return 'Pronto p/ Retirada';
      }
      return 'Saiu p/ Entrega';
    }
    
    // Para status 'pronto', diferenciar entre delivery e retirada
    if (status === 'pronto') {
      if (deliveryType === 'retirada' || deliveryType === 'pickup') {
        return 'Retirado';
      }
      return 'Entregue';
    }
    
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'em_producao': return 'Em Produção';
      case 'cancelado': return 'Cancelado';
      case 'rejeitado': return 'Rejeitado';
      default: return status;
    }
  };

  // Abrir modal de rejeição
  const openRejectModal = (order) => {
    setShowOrderDetails(false);  // Fechar o Dialog principal primeiro
    setRejectingOrder(order);
    setSelectedRejectReason('');
    setCustomRejectMessage('');
    setTimeout(() => {
      setShowRejectModal(true);
    }, 100);  // Pequeno delay para o Dialog fechar antes
  };

  // Confirmar rejeição do pedido
  const confirmRejectOrder = async () => {
    if (!rejectingOrder) return;
    if (!selectedRejectReason) {
      toast.error('Selecione um motivo para rejeitar o pedido');
      return;
    }
    
    setIsRejecting(true);
    try {
      const token = localStorage.getItem('token');
      const reasonData = REJECT_REASONS.find(r => r.id === selectedRejectReason);
      const customMessage = selectedRejectReason === 'outro' ? customRejectMessage : reasonData?.message;
      
      await axios.post(`${API}/orders/${rejectingOrder.id}/reject`, { 
        reason: selectedRejectReason,
        custom_message: customMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('❌ Pedido rejeitado com sucesso');
      setShowRejectModal(false);
      setShowOrderDetails(false);
      setSelectedOrder(null);
      setRejectingOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Error rejecting order:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao rejeitar pedido';
      toast.error(errorMsg);
    } finally {
      setIsRejecting(false);
    }
  };

  // Abrir modal de exclusão
  const openDeleteModal = (order) => {
    setShowOrderDetails(false);  // Fechar o Dialog principal primeiro
    setDeletingOrder(order);
    setTimeout(() => {
      setShowDeleteModal(true);
    }, 100);  // Pequeno delay para o Dialog fechar antes
  };

  // Confirmar exclusão do pedido
  const confirmDeleteOrder = async () => {
    if (!deletingOrder) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/orders/${deletingOrder.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('🗑️ Pedido excluído com sucesso');
      setShowDeleteModal(false);
      setShowOrderDetails(false);
      setSelectedOrder(null);
      setDeletingOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao excluir pedido';
      toast.error(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'pendente': 'em_producao',
      'em_producao': 'entrega',
      'entrega': 'pronto'
    };
    return statusFlow[currentStatus];
  };

  // Retorna o label do próximo status baseado no tipo de entrega
  const getNextStatusLabel = (currentStatus, deliveryType = 'delivery') => {
    const nextStatus = getNextStatus(currentStatus);
    return nextStatus ? getStatusLabel(nextStatus, deliveryType) : null;
  };

  const printOrder = (order) => {
    const fmt = { ...DEFAULT_PRINT_FORMAT, ...(printFormat || {}) };
    const b = (text, on) => (on ? `<b>${text}</b>` : `${text}`);
    // Debug: ver dados do pedido
    console.log('🖨️ Imprimindo pedido:', {
      id: order.id,
      delivery_fee: order.delivery_fee,
      delivery_zone_name: order.delivery_zone_name,
      subtotal: order.subtotal,
      total: order.total,
      address: order.address
    });
    
    const printWindow = window.open('', '_blank');
    const address = order.address || {};
    // Suportar address como string (pedidos WhatsApp) ou objeto (pedidos web)
    const addressStr = typeof address === 'string' 
      ? address 
      : [address.street, address.number, address.neighborhood, address.city].filter(Boolean).join(', ');
    
    // Obter bairro de qualquer fonte disponível
    const bairroName = order.delivery_zone_name || (typeof address === 'object' ? address.neighborhood : '') || '';
    
    // Calcular subtotal dos itens se não existir
    const calcularSubtotalItens = () => {
      if (!order.items || order.items.length === 0) return 0;
      return order.items.reduce((sum, item) => {
        const itemPrice = Number(item.price || item.unit_price || 0) * Number(item.quantity || 1);
        const additionalsPrice = (item.additionals || []).reduce((addSum, add) => {
          return addSum + (Number(add.price || add.unit_price || 0) * Number(add.quantity || 1));
        }, 0);
        return sum + itemPrice + additionalsPrice;
      }, 0);
    };
    
    // Obter taxa de entrega - calcular se não existir
    let taxaEntrega = null;
    if (order.delivery_fee !== undefined && order.delivery_fee !== null && order.delivery_fee > 0) {
      // Taxa está salva no pedido
      taxaEntrega = Number(order.delivery_fee);
    } else if (order.delivery_type === 'delivery' && order.total) {
      // Calcular taxa: Total - Subtotal dos itens - Desconto
      const subtotalItens = order.subtotal || calcularSubtotalItens();
      const desconto = Number(order.discount || 0) + Number(order.cashback_used || 0) + Number(order.coupon_discount || 0);
      const taxaCalculada = Number(order.total) - subtotalItens + desconto;
      // Só mostrar se a taxa calculada for positiva e fizer sentido
      if (taxaCalculada > 0 && taxaCalculada < 100) { // Limite razoável para taxa
        taxaEntrega = taxaCalculada;
      }
    }
    
    console.log('📊 Taxa calculada:', taxaEntrega);
    
    const formatAdditionals = (item) => {
      if (!item.additionals || item.additionals.length === 0) return '';
      return item.additionals.map(add => `
        <div style="padding-left: 20px; color: #666; font-size: 12px; ${fmt.bold_additionals ? 'font-weight: bold;' : ''}">
          + ${add.name || add.additional_name} ${(add.price || add.unit_price) ? `- R$ ${Number(add.price || add.unit_price).toFixed(2)}` : ''}
        </div>
      `).join('');
    };

    const paymentLabels = {
      'pix': 'PIX',
      'dinheiro': 'Dinheiro',
      'cartao': 'Cartão',
      'cartao_credito': 'Cartão de Crédito',
      'cartao_debito': 'Cartão de Débito',
      'vale_refeicao': 'Vale Refeição',
      'vale_alimentacao': 'Vale Alimentação'
    };
    const paymentMethod = paymentLabels[order.payment_method] || order.payment_method || 'Não informado';
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Pedido #${order.order_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 80mm; margin: 0 auto; }
            h1 { font-size: 20px; margin-bottom: 10px; text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .info { margin: 8px 0; font-size: 14px; }
            .info strong { display: inline-block; min-width: 80px; }
            .section-title { font-size: 16px; font-weight: bold; margin-top: 15px; margin-bottom: 10px; border-bottom: 1px dashed #ccc; padding-bottom: 5px; }
            .items { margin-top: 15px; }
            .item { padding: 8px 0; border-bottom: 1px solid #eee; }
            .item-name { font-weight: 500; }
            .item-price { float: right; }
            .item-obs { padding-left: 20px; color: #666; font-size: 12px; font-style: italic; margin-top: 4px; }
            .total-section { margin-top: 20px; border-top: 2px solid #000; padding-top: 10px; }
            .total { font-size: 18px; font-weight: bold; }
            .payment { margin-top: 10px; padding: 10px; background: #f5f5f5; border-radius: 4px; }
            .footer { margin-top: 20px; text-align: center; font-size: 12px; color: #666; border-top: 1px dashed #ccc; padding-top: 10px; }
          </style>
        </head>
        <body>
          <h1>${b('PEDIDO #' + order.order_number, fmt.bold_order_number)}</h1>
          ${fmt.header_text && fmt.header_text.trim() ? `<div style="text-align:center;font-weight:600;margin-bottom:10px;">${fmt.header_text.trim()}</div>` : ''}
          
          ${order.is_scheduled && order.scheduled_date && order.scheduled_time ? `
            <div style="background: #e0f2fe; border: 2px solid #0ea5e9; padding: 10px; margin-bottom: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 12px; font-weight: bold; color: #0369a1;">📅 PEDIDO AGENDADO</div>
              <div style="font-size: 18px; font-weight: bold; color: #0c4a6e;">${order.scheduled_date} às ${order.scheduled_time}</div>
            </div>
          ` : ''}
          
          <div class="info"><strong>Cliente:</strong> ${b(order.customer_name || 'Cliente', fmt.bold_customer_name)}</div>
          ${fmt.show_datetime ? `<div class="info"><strong>Data/Hora:</strong> ${new Date(order.created_at).toLocaleString('pt-BR')}</div>` : ''}
          ${fmt.show_phone && order.customer_phone ? `<div class="info"><strong>Telefone:</strong> ${order.customer_phone}</div>` : ''}
          
          ${order.delivery_type ? `<div class="info"><strong>Tipo:</strong> ${order.delivery_type === 'delivery' ? '🛵 Delivery' : order.delivery_type === 'mesa' ? '🍽️ Mesa' : '🏃 Retirada'}</div>` : ''}
          ${order.table_number ? `<div class="info"><strong>Mesa:</strong> ${order.table_number}</div>` : ''}
          
          ${fmt.show_address && addressStr ? `
            <div class="section-title">📍 Endereço de Entrega</div>
            <div class="info">${addressStr}</div>
            ${address.complement ? `<div class="info">Complemento: ${address.complement}</div>` : ''}
            ${address.reference ? `<div class="info">Referência: ${address.reference}</div>` : ''}
            ${bairroName ? `<div class="info"><strong>Bairro:</strong> ${bairroName}</div>` : ''}
            ${fmt.show_delivery_fee && taxaEntrega !== null && taxaEntrega !== undefined ? `<div class="info"><strong>🚚 Taxa de Entrega:</strong> R$ ${Number(taxaEntrega).toFixed(2)}</div>` : ''}
          ` : ''}
          
          <div class="section-title">📝 Itens do Pedido</div>
          <div class="items">
            ${order.items?.map(item => `
              <div class="item">
                <div>
                  <span class="item-name" style="${fmt.bold_product_name ? 'font-weight: bold;' : ''}">${item.quantity}x ${item.product_name || item.name}</span>
                  <span class="item-price">R$ ${(Number(item.price || item.unit_price || 0) * Number(item.quantity || 1)).toFixed(2)}</span>
                </div>
                ${formatAdditionals(item)}
                ${fmt.show_observations && (item.observation || item.notes) ? `<div class="item-obs" style="${fmt.bold_observations ? 'font-weight: bold;' : ''}">📌 Obs: ${item.observation || item.notes}</div>` : ''}
              </div>
            `).join('') || '<div class="item">Nenhum item</div>'}
          </div>
          
          ${order.notes ? `
            <div class="section-title">📋 Observações do Pedido</div>
            <div class="info">${order.notes}</div>
          ` : ''}
          
          <div class="total-section">
            ${fmt.show_subtotal && order.subtotal ? `<div class="info"><strong>Subtotal:</strong> R$ ${Number(order.subtotal).toFixed(2)}</div>` : ''}
            ${order.discount > 0 ? `<div class="info"><strong>Desconto:</strong> -R$ ${Number(order.discount).toFixed(2)}</div>` : ''}
            <div class="total" style="${fmt.bold_total ? 'font-weight: bold;' : 'font-weight: normal;'}">TOTAL: R$ ${Number(order.total || 0).toFixed(2)}</div>
            ${fmt.show_payment_method ? `<div class="payment">
              <strong>💳 Forma de Pagamento:</strong> ${paymentMethod}
              ${order.payment_method === 'dinheiro' && order.change_for ? `<br><strong>Troco para:</strong> R$ ${Number(order.change_for).toFixed(2)}` : ''}
            </div>` : ''}
          </div>
          
          ${fmt.show_footer ? `<div class="footer">
            <p>${(fmt.footer_message && fmt.footer_message.trim()) || 'Obrigado pela preferência!'}</p>
            <p>${new Date().toLocaleString('pt-BR')}</p>
          </div>` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatAddress = (address) => {
    if (!address) return '';
    // Suportar address como string (pedidos WhatsApp) ou objeto (pedidos web)
    if (typeof address === 'string') return address;
    if (typeof address !== 'object') return '';
    const parts = [
      address.street,
      address.number,
      address.neighborhood,
      address.city,
      address.state
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Mostrar erro se houver problema no carregamento
  if (loadError) {
    return (
      <div className="glass-card p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Erro ao carregar pedidos</h2>
        <p className="text-gray-400 mb-4">{loadError}</p>
        <button
          onClick={() => {
            setLoadError(null);
            setLoading(true);
            fetchOrders();
          }}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StockAlertsBanner token={localStorage.getItem('token')} />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-white">Pedidos</h1>
        <div className="flex items-center gap-3">
          {/* Toggle Auto-Accept */}
          <div className="flex items-center gap-1">
            <div 
              className="flex items-center gap-2 cursor-pointer group"
              onClick={toggleAutoAccept}
              title={autoAcceptEnabled ? 'Auto-aceitar pedidos ATIVO' : 'Auto-aceitar pedidos desativado'}
              data-testid="auto-accept-toggle"
            >
              <span className={`text-xs font-medium transition-colors ${autoAcceptEnabled ? 'text-emerald-400' : 'text-gray-500'}`}>
                Auto
              </span>
              <div className={`relative w-9 h-5 rounded-full transition-colors ${autoAcceptEnabled ? 'bg-emerald-500/30' : 'bg-gray-700'} ${autoAcceptLoading ? 'opacity-50' : ''}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
                  autoAcceptEnabled 
                    ? 'left-[18px] bg-emerald-400' 
                    : 'left-0.5 bg-gray-500'
                }`}>
                  {autoAcceptLoading ? (
                    <div className="w-full h-full rounded-full border-2 border-transparent border-t-white animate-spin" />
                  ) : (
                    <Zap size={10} className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${autoAcceptEnabled ? 'text-emerald-900' : 'text-gray-700'}`} />
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setShowAutoAcceptSettings(true); }}
              className={`p-1.5 rounded transition-all ${autoAcceptEnabled ? 'text-emerald-400 hover:bg-emerald-500/20' : 'text-gray-500 hover:bg-gray-700'}`}
              title="Configurar mensagem automática"
            >
              <Settings size={14} />
            </button>
          </div>
          
          <button
            onClick={toggleSound}
            className={`p-3 rounded-lg transition-all ${
              soundEnabled 
                ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/30' 
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600 border border-gray-600'
            }`}
            title={soundEnabled ? 'Notificação sonora ativada' : 'Notificação sonora desativada'}
            data-testid="sound-toggle-button"
          >
            <Bell size={18} className={soundEnabled ? 'animate-pulse' : ''} />
          </button>
          
          <button 
            onClick={() => setIsPDVOpen(true)}
            className="glass-button-primary"
            data-testid="open-pdv-button"
          >
            <PlusCircle size={18} className="mr-2" />
            Novo Pedido
          </button>
        </div>
      </div>

      {/* Modal de Configurações Auto-Accept */}
      <OrdersAutoAcceptModal
          autoAcceptMessage={autoAcceptMessage}
          autoAcceptSendWhatsApp={autoAcceptSendWhatsApp}
          autoAcceptNewOrderSound={autoAcceptNewOrderSound}
          setAutoAcceptNewOrderSound={setAutoAcceptNewOrderSound}
          saveAutoAcceptSettings={saveAutoAcceptSettings}
          savingAutoAcceptSettings={savingAutoAcceptSettings}
          setAutoAcceptMessage={setAutoAcceptMessage}
          setAutoAcceptSendWhatsApp={setAutoAcceptSendWhatsApp}
          setShowAutoAcceptSettings={setShowAutoAcceptSettings}
          showAutoAcceptSettings={showAutoAcceptSettings}
      />

      {/* Filters */}
      <div className="glass-card">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setDeliveryTypeFilter(''); setStatusFilter(''); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !deliveryTypeFilter && !statusFilter
                ? 'bg-red-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setDeliveryTypeFilter('delivery')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              deliveryTypeFilter === 'delivery'
                ? 'bg-red-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Delivery
          </button>
          <button
            onClick={() => setDeliveryTypeFilter('mesa')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              deliveryTypeFilter === 'mesa'
                ? 'bg-red-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Mesas
          </button>
          <button
            onClick={() => setDeliveryTypeFilter('retirada')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              deliveryTypeFilter === 'retirada'
                ? 'bg-red-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Retirada
          </button>
        </div>
      </div>

      {/* Desktop Kanban View */}
      <div className="hidden lg:block" data-testid="desktop-kanban-view">
        <KanbanBoard
          orders={orders}
          onPrint={printOrder}
          onUpdateStatus={updateOrderStatus}
          setOrders={setOrders}
          onCardClick={(order) => { setSelectedOrder(order); setShowOrderDetails(true); }}
          onEdit={startEditOrder}
          canEdit={canEditOrder}
        />
      </div>

      {/* Mobile List View */}
      <div className="lg:hidden space-y-4" data-testid="orders-list">
        {orders.map((order) => {
          const address = order.address || {};
          const addressStr = formatAddress(address);
          const orderDate = new Date(order.created_at);
          const timeStr = orderDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
          const dateStr = orderDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          
          return (
            <div 
              key={order.id} 
              className="glass-card hover:bg-white/15 transition-all cursor-pointer"
              onClick={() => { setSelectedOrder(order); setShowOrderDetails(true); }}
              data-testid={`order-card-${order.id}`}
            >
              {/* Order Header */}
              <div className="pb-4 border-b border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-white text-lg">
                        {order.customer_name || 'Cliente'}
                      </h3>
                      {order.source === 'whatsapp' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 border border-green-500/30 rounded-md text-green-400 text-xs font-medium">
                          <Bot className="w-3 h-3" />
                          IA
                        </span>
                      )}
                      {order.source === 'whatsapp_passive' && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 border border-purple-500/40 rounded-md text-purple-300 text-xs font-medium"
                          title="Pedido criado via Escuta Passiva (IA Externa)"
                          data-testid={`order-source-passive-${order.id}`}
                        >
                          <Ear className="w-3 h-3" />
                          Escuta
                        </span>
                      )}
                      {order.is_scheduled && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 border border-blue-500/30 rounded-md text-blue-400 text-xs font-medium">
                          <Calendar className="w-3 h-3" />
                          Agendado
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-300 mt-1">Pedido #{order.order_number}</p>
                    {order.is_scheduled && order.scheduled_date && order.scheduled_time && (
                      <p className="text-xs text-blue-400 mt-0.5 font-medium">
                        📅 {order.scheduled_date} às {order.scheduled_time}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">Em {dateStr} às {timeStr}</p>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status, order.delivery_type)}
                    </span>
                    {order.payment_method === 'pix' && (order.payment_status === 'paid' || order.status_pagamento === 'approved') && (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-green-500/20 text-green-400 border border-green-500/40 flex items-center gap-0.5">
                        <span className="text-[10px]">✓</span> pago
                      </span>
                    )}
                  </div>
                </div>
                
                {addressStr && (
                  <div className="mt-3 text-sm text-gray-300">
                    <p className="line-clamp-2">📍 {addressStr}</p>
                  </div>
                )}
                
                {order.table_number && (
                  <div className="mt-3 text-sm text-gray-300">
                    <p>🪑 Mesa {order.table_number}</p>
                  </div>
                )}
                
                {order.items && order.items.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {order.items.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="text-sm text-gray-300">
                        {item.quantity}x {item.product_name || item.name || item.additional_name || 'Item'}
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <p className="text-xs text-gray-400 italic">
                        + {order.items.length - 3} item(ns) adicional(is)
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Order Footer */}
              <div className="pt-4">
                {/* Delivery info - Bairro e Taxa */}
                {order.delivery_type === 'delivery' && (order.delivery_fee > 0 || order.delivery_zone_name) && (
                  <div className="mb-3 pb-3 border-b border-white/10">
                    <div className="flex items-center justify-between text-sm">
                      {order.delivery_zone_name && (
                        <span className="text-gray-400">
                          🏘️ {order.delivery_zone_name}
                        </span>
                      )}
                      {order.delivery_fee > 0 && (
                        <span className="text-orange-400 font-medium">
                          Taxa: R$ {(order.delivery_fee || 0).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Payment Method, Change & Notes */}
                <div className="mb-3 pb-3 border-b border-white/10 space-y-2">
                  {/* Forma de Pagamento */}
                  {order.payment_method && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400">💳</span>
                      <span className="text-gray-300 capitalize">
                        {order.payment_method === 'dinheiro' && '💵 Dinheiro'}
                        {order.payment_method === 'pix' && '📱 PIX'}
                        {order.payment_method === 'cartao' && '💳 Cartão'}
                        {order.payment_method === 'credito' && '💳 Crédito'}
                        {!['dinheiro', 'pix', 'cartao', 'credito'].includes(order.payment_method) && order.payment_method}
                      </span>
                      {/* Troco */}
                      {order.payment_method === 'dinheiro' && order.change_for > 0 && (
                        <span className="text-yellow-400 text-xs ml-auto">
                          Troco p/ R$ {Number(order.change_for).toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Observações */}
                  {order.notes && (
                    <div className="text-sm">
                      <span className="text-yellow-400">📝 Obs:</span>
                      <span className="text-gray-300 ml-1 italic">{order.notes}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-white">
                      R$ {order.total?.toFixed(2) || '0.00'}
                    </span>
                    {order.payment_method === 'pix' && (order.payment_status === 'paid' || order.status_pagamento === 'approved') && (
                      <span className="px-2 py-1 rounded-md text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/50 flex items-center gap-1">
                        ✓ PIX Pago
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {canEditOrder(order) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditOrder(order);
                        }}
                        className="p-2 border border-amber-500/40 text-amber-300 bg-amber-500/10 rounded-lg hover:bg-amber-500/20 hover:text-amber-200 transition-colors flex items-center justify-center shrink-0"
                        data-testid={`edit-order-${order.id}`}
                        title="Editar pedido"
                        aria-label="Editar pedido"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        printOrder(order);
                      }}
                      className="px-3 py-2 border border-white/20 text-gray-300 rounded-lg text-sm font-medium hover:bg-white/10 hover:text-white transition-colors flex items-center gap-1"
                      data-testid={`print-order-${order.id}`}
                    >
                      <FileText size={16} />
                      Imprimir
                    </button>
                    
                    {getNextStatus(order.status) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateOrderStatus(order.id, getNextStatus(order.status));
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                        data-testid={`update-status-${order.id}`}
                      >
                        {getNextStatusLabel(order.status, order.delivery_type)} →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {orders.length === 0 && (
        <div className="glass-card text-center py-12">
          <ShoppingCart size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Nenhum pedido encontrado
          </h3>
          <p className="text-gray-400 mb-6">
            {deliveryTypeFilter 
              ? `Não há pedidos do tipo "${deliveryTypeFilter}"`
              : 'Crie seu primeiro pedido para começar'
            }
          </p>
          <button 
            onClick={() => setIsPDVOpen(true)}
            className="glass-button-primary"
          >
            Criar Primeiro Pedido
          </button>
        </div>
      )}

      {/* Order Details Modal */}
      <OrdersDetailsModal
          formatAddress={formatAddress}
          getNextStatus={getNextStatus}
          getNextStatusLabel={getNextStatusLabel}
          getStatusColor={getStatusColor}
          getStatusLabel={getStatusLabel}
          loadingHistory={loadingHistory}
          openDeleteModal={openDeleteModal}
          openRejectModal={openRejectModal}
          orderHistory={orderHistory}
          printOrder={printOrder}
          selectedOrder={selectedOrder}
          setShowOrderDetails={setShowOrderDetails}
          showOrderDetails={showOrderDetails}
          updateOrderStatus={updateOrderStatus}
      />

      {/* PDV Screen */}
      {isPDVOpen && (
        <PDVScreen
          editingOrder={editingOrder}
          onClose={() => {
            setIsPDVOpen(false);
            setEditingOrder(null);
            fetchOrders();
          }}
        />
      )}

      {/* Modal de Seleção de Entregador */}
      {showDriverModal && (
        <OrdersDriverModal
          deliveryDrivers={deliveryDrivers}
          handleSelectDriver={handleSelectDriver}
          notifyingDriver={notifyingDriver}
          pendingDeliveryOrder={pendingDeliveryOrder}
          setPendingDeliveryOrder={setPendingDeliveryOrder}
          setShowDriverModal={setShowDriverModal}
        />
      )}

      {/* Modal de Rejeição de Pedido */}
      {showRejectModal && rejectingOrder && (
        <OrdersRejectModal
          REJECT_REASONS={REJECT_REASONS}
          confirmRejectOrder={confirmRejectOrder}
          customRejectMessage={customRejectMessage}
          isRejecting={isRejecting}
          rejectingOrder={rejectingOrder}
          selectedRejectReason={selectedRejectReason}
          setCustomRejectMessage={setCustomRejectMessage}
          setRejectingOrder={setRejectingOrder}
          setSelectedRejectReason={setSelectedRejectReason}
          setShowRejectModal={setShowRejectModal}
        />
      )}

      {/* Modal de Exclusão de Pedido */}
      {showDeleteModal && deletingOrder && (
        <OrdersDeleteModal
          confirmDeleteOrder={confirmDeleteOrder}
          deletingOrder={deletingOrder}
          isDeleting={isDeleting}
          setDeletingOrder={setDeletingOrder}
          setShowDeleteModal={setShowDeleteModal}
        />
      )}
    </div>
  );
};

// Wrapper com Error Boundary para capturar erros de renderização
const OrdersContentWithErrorBoundary = (props) => (
  <OrdersErrorBoundary>
    <OrdersContent {...props} />
  </OrdersErrorBoundary>
);

export default OrdersContentWithErrorBoundary;
